import { Box, Typography, Tooltip, Stack, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import NoPrgramFound from '../../../assets/no-program-found.svg';
import LoaderComponent from 'components/Loader';
import { useEffect, useRef, useState } from 'react';
import { ECElementEvent } from 'echarts';
import HighlightedText from "components/GlobalSearch/HighlightText";
import { InfoTooltip, TooltipLabel } from 'components/Tooltip/InfoTooltip';
import { BarChartWidgetProps } from '../type';
import { defaultOptions, getBarChartOptions } from '../chartWidgetUtility';
import { useTheme } from "@mui/material/styles";

const defaultHeight = 350;

const getHeightPx = (value: number, heightDifferencePercent: number) => {
    return `${value + value * (heightDifferencePercent / 100)}px`;
};


interface BarChartWidgetNewProps extends BarChartWidgetProps {
    hasTooltip?: boolean;
    tooltipLabel?: TooltipLabel;
}

const BarChartWidgetNew = ({
    isLoading = false,
    isCost = false,
    isPBI = false,
    isPercentage = false,
    isOverlayWidget = false,
    isInProgress = false,
    title = '',
    data,
    seriesData,
    minTitleHeight = '50.5px',
    handleEvent,
    legendLabel = '',
    yAxisOptions = {},
    xAxisOptions = {},
    heightDifferencePercent = 0,
    containerProps,
    children,
    isEvent = true,
    dateRange = [],
    isRMCost = false,
    hasTooltip = true,
    tooltipLabel
}: Readonly<BarChartWidgetNewProps>) => {
    const theme = useTheme();
    const colors = theme.palette;
    const [windowSize, setWindowSize] = useState(window.innerWidth);
    const [gridLeft, setGridLeft] = useState('5%');
    const [minChartHeight, setMinChartHeight] = useState(getHeightPx(defaultHeight, heightDifferencePercent));
    const chartContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleResize = debounce(() => {
            if (!chartContainerRef.current) return;

            const containerWidth = chartContainerRef.current.offsetWidth;

            let calculatedLeft: string;

            if (containerWidth < 420) {
                calculatedLeft = '5%';
            } else if (containerWidth < 920) {
                calculatedLeft = '9%';
            } else if (containerWidth < 1260) {
                calculatedLeft = '7%';
            } else {
                calculatedLeft = '5%';
            }

            let calculatedChartHeight;

            if (containerWidth < 420) {
                calculatedChartHeight = getHeightPx(680, heightDifferencePercent);
            } else if (containerWidth < 500) {
                calculatedChartHeight = getHeightPx(600, heightDifferencePercent);
            } else if (containerWidth < 685) {
                calculatedChartHeight = getHeightPx(550, heightDifferencePercent);
            } else if (containerWidth < 920) {
                calculatedChartHeight = getHeightPx(450, heightDifferencePercent);
            } else {
                calculatedChartHeight = getHeightPx(defaultHeight, heightDifferencePercent);
            }

            setGridLeft(calculatedLeft);
            setMinChartHeight(calculatedChartHeight);
            setWindowSize(window.innerWidth);
        }, 200);

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [heightDifferencePercent]);

    const otherOptions = {
        isCost,
        isPBI,
        legendLabel,
        isPercentage,
        yAxisOptions,
        xAxisOptions,
        isNewTheme: true,
        colors
    };

    const result = getBarChartOptions(
        data ?? [],
        seriesData,
        otherOptions,
        isEvent,
        dateRange,
        chartContainerRef?.current?.offsetWidth,
        isRMCost
    );

    const adjustedOption: any = !data?.length
        ? {
                ...defaultOptions,
                backgroundColor: colors.widget.background.card
          }
        : {
                ...result,
                grid: {
                    ...result.grid,
                    left: gridLeft
                },
                ...(chartContainerRef?.current?.offsetWidth &&
                    chartContainerRef.current.offsetWidth < 650 && {
                        xAxis: {
                            ...result.xAxis,
                            axisLabel: {
                                ...result.xAxis.axisLabel,
                            }
                        }
                    })
          };

    return (
        <Box display="flex" flexGrow={1} height="100%" position="relative">
            <Stack
                minHeight="100px"
                p={2}
                bgcolor={`${colors.widget.background.card} !important`}
                fontFamily="Montserrat"
                borderRadius="5px"
                flexGrow={1}
                // {...(handleEvent && { onClick: handleEvent, sx: { cursor: 'pointer' } })}
                {...containerProps}
            >
                <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" pb={1}>
                    <Stack direction="row" alignItems="center">
                        <Typography
                            variant={isOverlayWidget ? 'overlayWidgetTitle' : 'widgetTitle'}
                            sx={{ color: `${colors.widget.text.title} !important` }}
                        >
                            <HighlightedText text={title} />
                        </Typography>

                        {hasTooltip && tooltipLabel && <InfoTooltip label={tooltipLabel} />}
                    </Stack>

                    <Box display='flex' gap={1}>
                        {children}
                    </Box>
                </Stack>

                <Stack height={minChartHeight} flexGrow={1} justifyContent="center" key={windowSize} ref={chartContainerRef}>
                    {!isLoading ? (
                        <ReactECharts
                            option={adjustedOption}
                            style={{ width: '100%', height: '99%' }}
                            opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
                            notMerge={true}
                            lazyUpdate={true}
                            onChartReady={() => {
                                console.log('chart')
                            }}
                            onEvents={{
                                click: (params: ECElementEvent) => {
                                    if(handleEvent){
                                        handleEvent(params);
                                    }
                                }
                            }}
                        />
                    ) : (
                        <LoaderComponent />
                    )}
                </Stack>

                {isInProgress && (
                    <Box position="absolute" right={15} bottom={10}>
                        <Tooltip title="In Progress/Validation">
                            <img
                                src={NoPrgramFound}
                                style={{
                                    height: '20px',
                                    width: '20px'
                                }}
                            />
                        </Tooltip>
                    </Box>
                )}
            </Stack>
        </Box>
    );
};

export default BarChartWidgetNew;